import {omneticApi} from '@dms/api/base';
import {UuidResponseBodySchema} from '@dms/api/shared';

import {
  AddPurchasePaymentApiArg,
  AddPurchasePaymentApiResponse,
  WithdrawSaleBalancePaymentApiArg,
  WithdrawSaleBalancePaymentApiResponse,
  CancelPurchasePaymentApiArg,
  CancelPurchasePaymentApiResponse,
  EditPurchasePaymentApiArg,
  EditPurchasePaymentApiResponse,
  IssuePurchasePaymentApiArg,
  IssuePurchasePaymentApiResponse,
  PayPurchasePaymentV2ApiArg,
  PayPurchasePaymentV2ApiResponse,
  RemovePurchasePaymentApiArg,
  RemovePurchasePaymentApiResponse,
  WithdrawPurchasePaymentApiArg,
  WithdrawPurchasePaymentApiResponse,
  AddPurchasePaymentApiArgSchema,
  CancelPurchasePaymentApiArgSchema,
  EditPurchasePaymentApiArgSchema,
  IssuePurchasePaymentApiArgSchema,
  RemovePurchasePaymentApiArgSchema,
  WithdrawPurchasePaymentApiArgSchema,
  AddSaleDepositPaymentApiArg,
  AddSaleDepositPaymentApiArgSchema,
  AddSaleDepositPaymentApiResponse,
  CancelSaleDepositPaymentApiArg,
  CancelSaleDepositPaymentApiArgSchema,
  CancelSaleDepositPaymentApiResponse,
  EditSaleDepositPaymentApiArg,
  EditSaleDepositPaymentApiArgSchema,
  EditSaleDepositPaymentApiResponse,
  IssueSaleDepositPaymentApiArg,
  IssueSaleDepositPaymentApiArgSchema,
  IssueSaleDepositPaymentApiResponse,
  PaySaleDepositPaymentV2ApiArgSchema,
  PaySaleDepositPaymentV2ApiArg,
  PaySaleDepositPaymentV2ApiResponse,
  RemoveSaleDepositPaymentApiArg,
  RemoveSaleDepositPaymentApiArgSchema,
  RemoveSaleDepositPaymentApiResponse,
  WithdrawSaleDepositPaymentApiArg,
  WithdrawSaleDepositPaymentApiArgSchema,
  WithdrawSaleDepositPaymentApiResponse,
  WithdrawPartialSaleDepositPaymentApiArg,
  WithdrawPartialSaleDepositPaymentApiArgSchema,
  WithdrawPartialSaleDepositPaymentApiResponse,
  CancelSaleBalancePaymentApiArg,
  CancelSaleBalancePaymentApiArgSchema,
  CancelSaleBalancePaymentApiResponse,
  EditSaleBalancePaymentApiArg,
  EditSaleBalancePaymentApiArgSchema,
  EditSaleBalancePaymentApiResponse,
  IssueSaleBalancePaymentApiArg,
  IssueSaleBalancePaymentApiArgSchema,
  IssueSaleBalancePaymentApiResponse,
  PaySaleBalancePaymentV2ApiArgSchema,
  PaySaleBalancePaymentV2ApiArg,
  PaySaleBalancePaymentV2ApiResponse,
  WithdrawSaleBalancePaymentApiArgSchema,
  WithdrawPartialSaleBalancePaymentApiArg,
  WithdrawPartialSaleBalancePaymentApiArgSchema,
  WithdrawPartialSaleBalancePaymentApiResponse,
  CreateFullCorrectiveTaxDocumentApiArg,
  CreateFullCorrectiveTaxDocumentApiArgSchema,
  CreateFullCorrectiveTaxDocumentApiResponse,
  CreatePartialCorrectiveTaxDocumentApiArg,
  CreatePartialCorrectiveTaxDocumentApiArgSchema,
  CreatePartialCorrectiveTaxDocumentApiResponse,
  CreatePaymentFileApiArg,
  CreatePaymentFileApiArgSchema,
  CreatePaymentFileApiResponse,
  CreateTaxDocumentApiArg,
  CreateTaxDocumentApiArgSchema,
  CreateTaxDocumentApiResponse,
  CreateBrokerageInternalInvoiceDocumentDocumentApiArg,
  CreateBrokerageInternalInvoiceDocumentDocumentApiArgSchema,
  CreateBrokerageInternalInvoiceDocumentDocumentApiResponse,
  CreateBrokerageInternalInvoiceDocumentWithVatDocumentApiArg,
  CreateBrokerageInternalInvoiceDocumentWithVatDocumentApiArgSchema,
  CreateBrokerageInternalInvoiceDocumentWithVatDocumentApiResponse,
  CreateInternalInvoiceDocumentDocumentApiArg,
  CreateInternalInvoiceDocumentDocumentApiArgSchema,
  CreateInternalInvoiceDocumentDocumentApiResponse,
  CreateInternalInvoiceDocumentWithVatDocumentApiArg,
  CreateInternalInvoiceDocumentWithVatDocumentApiArgSchema,
  CreateInternalInvoiceDocumentWithVatDocumentApiResponse,
  PayPurchasePaymentV2ApiArgSchema,
} from './types';

export const businessCasePaymentsApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    addPurchasePayment: build.mutation<AddPurchasePaymentApiResponse, AddPurchasePaymentApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/purchase/add`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, queryArgs) => [
        {type: 'offer', id: queryArgs.orderId},
        'Checkout',
      ],
      extraOptions: {
        requestSchema: AddPurchasePaymentApiArgSchema,
        responseSchema: UuidResponseBodySchema,
      },
    }),
    cancelPurchasePayment: build.mutation<
      CancelPurchasePaymentApiResponse,
      CancelPurchasePaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/purchase/${queryArg.paymentId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        'BusinessCaseDetail',
        'documentsCount',
        'BusinessCaseActions',
        {type: 'BusinessCaseActivity', id: queryArg.businessCaseId},
      ],
      extraOptions: {
        requestSchema: CancelPurchasePaymentApiArgSchema,
      },
    }),
    editPurchasePayment: build.mutation<EditPurchasePaymentApiResponse, EditPurchasePaymentApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/purchase/${queryArg.paymentId}/edit`,
        method: 'POST',
        body: queryArg.editPaymentRequestBody,
      }),
      invalidatesTags: (result, error, queryArgs) => [
        'Checkout',
        {type: 'offer', id: queryArgs.orderId},
      ],
      extraOptions: {
        requestSchema: EditPurchasePaymentApiArgSchema,
      },
    }),
    issuePurchasePayment: build.mutation<
      IssuePurchasePaymentApiResponse,
      IssuePurchasePaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/purchase/${queryArg.paymentId}/issue`,
        method: 'POST',
        body: queryArg.issuePaymentRequestBody,
        params: {validateMandatoryFields: queryArg.validateMandatoryFields},
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        {type: 'Checkout'},
        {type: 'BusinessCaseDetail', id: queryArg.businessCaseId},
        {type: 'BusinessCaseActions', id: queryArg.businessCaseId},
        {type: 'BusinessCaseActivity', id: queryArg.businessCaseId},
        {type: 'documentsCount', id: queryArg.businessCaseId},
      ],
      extraOptions: {
        requestSchema: IssuePurchasePaymentApiArgSchema,
      },
    }),
    payPurchasePaymentV2: build.mutation<
      PayPurchasePaymentV2ApiResponse,
      PayPurchasePaymentV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/purchase/${queryArg.paymentId}/pay`,
        method: 'PUT',
        body: queryArg.paySaleBusinessCaseRequestBody,
      }),
      invalidatesTags: (result, error, queryArgs) => [
        {type: 'offer', id: queryArgs.orderId},
        'Checkout',
        'invoicePayment',
        'invoiceProformaPaymentList',
        'BusinessCaseDetail',
        'BusinessCaseActions',
        'BusinessCaseActivity',
        'InterestActivity',
        'documentsCount',
        'paymentPrescription',
      ],
      extraOptions: {
        requestSchema: PayPurchasePaymentV2ApiArgSchema,
      },
    }),
    removePurchasePayment: build.mutation<
      RemovePurchasePaymentApiResponse,
      RemovePurchasePaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/purchase/${queryArg.paymentId}/remove`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        {type: 'Checkout'},
      ],
      extraOptions: {
        requestSchema: RemovePurchasePaymentApiArgSchema,
      },
    }),
    withdrawPurchasePayment: build.mutation<
      WithdrawPurchasePaymentApiResponse,
      WithdrawPurchasePaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/purchase/${queryArg.paymentId}/withdraw`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        'invoicePayment',
        'invoiceProformaPaymentList',
        'paymentPrescription',
        'BusinessCaseDetail',
        'BusinessCaseActions',
        'documentsCount',
        'BusinessCaseActivity',
      ],
      extraOptions: {
        requestSchema: WithdrawPurchasePaymentApiArgSchema,
      },
    }),
    addSaleDepositPayment: build.mutation<
      AddSaleDepositPaymentApiResponse,
      AddSaleDepositPaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/deposit/add`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        'documentsCount',
      ],
      extraOptions: {
        requestSchema: AddSaleDepositPaymentApiArgSchema,
        responseSchema: UuidResponseBodySchema,
      },
    }),
    cancelSaleDepositPayment: build.mutation<
      CancelSaleDepositPaymentApiResponse,
      CancelSaleDepositPaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/deposit/${queryArg.paymentId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        'BusinessCaseDetail',
        'documentsCount',
        'BusinessCaseActions',
        {type: 'BusinessCaseActivity', id: queryArg.businessCaseId},
      ],
      extraOptions: {
        requestSchema: CancelSaleDepositPaymentApiArgSchema,
      },
    }),
    editSaleDepositPayment: build.mutation<
      EditSaleDepositPaymentApiResponse,
      EditSaleDepositPaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/deposit/${queryArg.paymentId}/edit`,
        method: 'POST',
        body: queryArg.editPaymentRequestBody,
      }),
      invalidatesTags: (result, error, queryArgs) => [
        'Checkout',
        {type: 'offer', id: queryArgs.orderId},
      ],
      extraOptions: {
        requestSchema: EditSaleDepositPaymentApiArgSchema,
      },
    }),
    issueSaleDepositPayment: build.mutation<
      IssueSaleDepositPaymentApiResponse,
      IssueSaleDepositPaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/deposit/${queryArg.paymentId}/issue`,
        method: 'POST',
        body: queryArg.issuePaymentRequestBody,
        params: {validateMandatoryFields: queryArg.validateMandatoryFields},
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        {type: 'Checkout'},
        {type: 'BusinessCaseDetail', id: queryArg.businessCaseId},
        {type: 'BusinessCaseActions', id: queryArg.businessCaseId},
        {type: 'BusinessCaseActivity', id: queryArg.businessCaseId},
        {type: 'documentsCount', id: queryArg.businessCaseId},
      ],
      extraOptions: {
        requestSchema: IssueSaleDepositPaymentApiArgSchema,
      },
    }),
    paySaleDepositPaymentV2: build.mutation<
      PaySaleDepositPaymentV2ApiResponse,
      PaySaleDepositPaymentV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/deposit/${queryArg.paymentId}/pay`,
        method: 'PUT',
        body: queryArg.paySaleBusinessCaseRequestBody,
      }),
      invalidatesTags: (result, error, queryArgs) => [
        {type: 'offer', id: queryArgs.orderId},
        'Checkout',
        'invoicePayment',
        'invoiceProformaPaymentList',
        'BusinessCaseDetail',
        'BusinessCaseActions',
        'BusinessCaseActivity',
        'InterestActivity',
        'documentsCount',
        'paymentPrescription',
      ],
      extraOptions: {
        requestSchema: PaySaleDepositPaymentV2ApiArgSchema,
      },
    }),
    removeSaleDepositPayment: build.mutation<
      RemoveSaleDepositPaymentApiResponse,
      RemoveSaleDepositPaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/deposit/${queryArg.paymentId}/remove`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        'documentsCount',
        'BusinessCaseActions',
        'BusinessCaseDetail',
        'Checkout',
      ],
      extraOptions: {
        requestSchema: RemoveSaleDepositPaymentApiArgSchema,
      },
    }),
    withdrawSaleDepositPayment: build.mutation<
      WithdrawSaleDepositPaymentApiResponse,
      WithdrawSaleDepositPaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/deposit/${queryArg.paymentId}/withdraw`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        'invoicePayment',
        'invoiceProformaPaymentList',
        'paymentPrescription',
        'BusinessCaseDetail',
        'BusinessCaseActions',
        'documentsCount',
        'BusinessCaseActivity',
      ],
      extraOptions: {
        requestSchema: WithdrawSaleDepositPaymentApiArgSchema,
      },
    }),
    // withdrawalPayment
    withdrawPartialSaleDepositPayment: build.mutation<
      WithdrawPartialSaleDepositPaymentApiResponse,
      WithdrawPartialSaleDepositPaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/deposit/${queryArg.paymentId}/withdraw/${queryArg.transactionId}`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        'Checkout',
        'invoicePayment',
        'invoiceProformaPaymentList',
        'BusinessCaseDetail',
        'BusinessCaseActions',
        'documentsCount',
        'paymentPrescription',
        'BusinessCaseActivity',
      ],
      extraOptions: {
        requestSchema: WithdrawPartialSaleDepositPaymentApiArgSchema,
      },
    }),
    cancelSaleBalancePayment: build.mutation<
      CancelSaleBalancePaymentApiResponse,
      CancelSaleBalancePaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/balance/${queryArg.paymentId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        'BusinessCaseDetail',
        'documentsCount',
        'BusinessCaseActions',
        {type: 'BusinessCaseActivity', id: queryArg.businessCaseId},
      ],
      extraOptions: {
        requestSchema: CancelSaleBalancePaymentApiArgSchema,
      },
    }),
    editSaleBalancePayment: build.mutation<
      EditSaleBalancePaymentApiResponse,
      EditSaleBalancePaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/balance/${queryArg.paymentId}/edit`,
        method: 'POST',
        body: queryArg.editPaymentRequestBody,
      }),
      invalidatesTags: (result, error, queryArgs) => [
        'Checkout',
        {type: 'offer', id: queryArgs.orderId},
      ],
      extraOptions: {
        requestSchema: EditSaleBalancePaymentApiArgSchema,
      },
    }),
    issueSaleBalancePayment: build.mutation<
      IssueSaleBalancePaymentApiResponse,
      IssueSaleBalancePaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/balance/${queryArg.paymentId}/issue`,
        method: 'POST',
        body: queryArg.issuePaymentRequestBody,
        params: {validateMandatoryFields: queryArg.validateMandatoryFields},
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        {type: 'Checkout'},
        {type: 'BusinessCaseDetail', id: queryArg.businessCaseId},
        {type: 'BusinessCaseActions', id: queryArg.businessCaseId},
        {type: 'BusinessCaseActivity', id: queryArg.businessCaseId},
        {type: 'documentsCount', id: queryArg.businessCaseId},
      ],
      extraOptions: {
        requestSchema: IssueSaleBalancePaymentApiArgSchema,
      },
    }),
    paySaleBalancePaymentV2: build.mutation<
      PaySaleBalancePaymentV2ApiResponse,
      PaySaleBalancePaymentV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/balance/${queryArg.paymentId}/pay`,
        method: 'PUT',
        body: queryArg.paySaleBusinessCaseRequestBody,
      }),
      extraOptions: {
        requestSchema: PaySaleBalancePaymentV2ApiArgSchema,
      },
      invalidatesTags: (result, error, queryArgs) => [
        {type: 'offer', id: queryArgs.orderId},
        'Checkout',
        'invoicePayment',
        'invoiceProformaPaymentList',
        'BusinessCaseDetail',
        'BusinessCaseActions',
        'BusinessCaseActivity',
        'InterestActivity',
        'documentsCount',
        'paymentPrescription',
      ],
    }),
    withdrawSaleBalancePayment: build.mutation<
      WithdrawSaleBalancePaymentApiResponse,
      WithdrawSaleBalancePaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/balance/${queryArg.paymentId}/withdraw`,
        method: 'POST',
      }),
      extraOptions: {
        requestSchema: WithdrawSaleBalancePaymentApiArgSchema,
      },
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        'invoicePayment',
        'invoiceProformaPaymentList',
        'paymentPrescription',
        'BusinessCaseDetail',
        'BusinessCaseActions',
        'documentsCount',
        'BusinessCaseActivity',
      ],
    }),
    // withdrawalPayment
    withdrawPartialSaleBalancePayment: build.mutation<
      WithdrawPartialSaleBalancePaymentApiResponse,
      WithdrawPartialSaleBalancePaymentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/balance/${queryArg.paymentId}/withdraw/${queryArg.transactionId}`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        'Checkout',
        'invoicePayment',
        'invoiceProformaPaymentList',
        'BusinessCaseDetail',
        'BusinessCaseActions',
        'documentsCount',
        'paymentPrescription',
        'BusinessCaseActivity',
      ],
      extraOptions: {
        requestSchema: WithdrawPartialSaleBalancePaymentApiArgSchema,
      },
    }),
    createFullCorrectiveTaxDocument: build.mutation<
      CreateFullCorrectiveTaxDocumentApiResponse,
      CreateFullCorrectiveTaxDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/payment/${queryArg.paymentId}/full-corrective-tax-document`,
        method: 'POST',
        body: queryArg.correctiveTaxDocumentRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        'invoicePayment',
        'Checkout',
        'documentsCount',
        'invoiceProformaPaymentList',
        'paymentPrescription',
        'SaleVehicle',
        'BusinessCaseDetail',
        'BusinessCaseActivity',
      ],
      extraOptions: {
        requestSchema: CreateFullCorrectiveTaxDocumentApiArgSchema,
      },
    }),
    createPartialCorrectiveTaxDocument: build.mutation<
      CreatePartialCorrectiveTaxDocumentApiResponse,
      CreatePartialCorrectiveTaxDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/payment/${queryArg.paymentId}/partial-corrective-tax-document`,
        method: 'POST',
        body: queryArg.correctiveTaxDocumentRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'offer', id: queryArg.orderId},
        'invoicePayment',
        'Checkout',
        'documentsCount',
        'invoiceProformaPaymentList',
        'paymentPrescription',
        'SaleVehicle',
        'BusinessCaseDetail',
        'BusinessCaseActivity',
      ],
      extraOptions: {
        requestSchema: CreatePartialCorrectiveTaxDocumentApiArgSchema,
      },
    }),
    createPaymentFile: build.mutation<CreatePaymentFileApiResponse, CreatePaymentFileApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/payment/${queryArg.paymentId}/file`,
        method: 'POST',
        body: queryArg.createPaymentFileRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [{type: 'offer', id: queryArg.orderId}],
      extraOptions: {
        requestSchema: CreatePaymentFileApiArgSchema,
      },
    }),
    createTaxDocument: build.mutation<CreateTaxDocumentApiResponse, CreateTaxDocumentApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/transaction/${queryArg.transactionId}/create-tax-document`,
        method: 'POST',
        body: queryArg.createTaxDocumentRequestBody,
      }),
      invalidatesTags: ['invoiceProformaPaymentList'],
      extraOptions: {
        requestSchema: CreateTaxDocumentApiArgSchema,
        responseSchema: UuidResponseBodySchema,
      },
    }),
    createBrokerageInternalInvoiceDocumentDocument: build.mutation<
      CreateBrokerageInternalInvoiceDocumentDocumentApiResponse,
      CreateBrokerageInternalInvoiceDocumentDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/balance/${queryArg.paymentId}/brokerage-internal-invoice-document`,
        method: 'PUT',
        body: queryArg.createInternalInvoiceDocumentRequestBody,
      }),
      invalidatesTags: (result, error, queryArgs) => [
        {type: 'offer', id: queryArgs.orderId},
        'Checkout',
        'documentsCount',
      ],
      extraOptions: {
        requestSchema: CreateBrokerageInternalInvoiceDocumentDocumentApiArgSchema,
      },
    }),
    createBrokerageInternalInvoiceDocumentWithVatDocument: build.mutation<
      CreateBrokerageInternalInvoiceDocumentWithVatDocumentApiResponse,
      CreateBrokerageInternalInvoiceDocumentWithVatDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/balance/${queryArg.paymentId}/brokerage-internal-invoice-document-with-vat`,
        method: 'PUT',
        body: queryArg.createInternalInvoiceDocumentRequestBody,
      }),
      invalidatesTags: (result, error, queryArgs) => [
        {type: 'offer', id: queryArgs.orderId},
        'Checkout',
        'documentsCount',
      ],
      extraOptions: {
        requestSchema: CreateBrokerageInternalInvoiceDocumentWithVatDocumentApiArgSchema,
      },
    }),
    createInternalInvoiceDocumentDocument: build.mutation<
      CreateInternalInvoiceDocumentDocumentApiResponse,
      CreateInternalInvoiceDocumentDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/balance/${queryArg.paymentId}/internal-invoice-document`,
        method: 'PUT',
        body: queryArg.createInternalInvoiceDocumentRequestBody,
      }),
      invalidatesTags: (result, error, queryArgs) => [
        {type: 'offer', id: queryArgs.orderId},
        'Checkout',
        'documentsCount',
      ],
      extraOptions: {
        requestSchema: CreateInternalInvoiceDocumentDocumentApiArgSchema,
      },
    }),
    createInternalInvoiceDocumentWithVatDocument: build.mutation<
      CreateInternalInvoiceDocumentWithVatDocumentApiResponse,
      CreateInternalInvoiceDocumentWithVatDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/order/${queryArg.orderId}/balance/${queryArg.paymentId}/internal-invoice-document-with-vat`,
        method: 'PUT',
        body: queryArg.createInternalInvoiceDocumentRequestBody,
      }),
      invalidatesTags: (result, error, queryArgs) => [
        {type: 'offer', id: queryArgs.orderId},
        'Checkout',
        'documentsCount',
      ],
      extraOptions: {
        requestSchema: CreateInternalInvoiceDocumentWithVatDocumentApiArgSchema,
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  // Purchase payment types
  useAddPurchasePaymentMutation,
  useCancelPurchasePaymentMutation,
  useEditPurchasePaymentMutation,
  useIssuePurchasePaymentMutation,
  usePayPurchasePaymentV2Mutation,
  useRemovePurchasePaymentMutation,
  useWithdrawPurchasePaymentMutation,
  // Deposit payment types
  useAddSaleDepositPaymentMutation,
  useCancelSaleDepositPaymentMutation,
  useEditSaleDepositPaymentMutation,
  useIssueSaleDepositPaymentMutation,
  usePaySaleDepositPaymentV2Mutation,
  useRemoveSaleDepositPaymentMutation,
  useWithdrawSaleDepositPaymentMutation,
  useWithdrawPartialSaleDepositPaymentMutation,
  // Balance payment types
  useCancelSaleBalancePaymentMutation,
  useEditSaleBalancePaymentMutation,
  useIssueSaleBalancePaymentMutation,
  usePaySaleBalancePaymentV2Mutation,
  useWithdrawSaleBalancePaymentMutation,
  useWithdrawPartialSaleBalancePaymentMutation,
  // Document creation types
  useCreateFullCorrectiveTaxDocumentMutation,
  useCreatePartialCorrectiveTaxDocumentMutation,
  useCreatePaymentFileMutation,
  useCreateTaxDocumentMutation,
  useCreateBrokerageInternalInvoiceDocumentDocumentMutation,
  useCreateBrokerageInternalInvoiceDocumentWithVatDocumentMutation,
  useCreateInternalInvoiceDocumentDocumentMutation,
  useCreateInternalInvoiceDocumentWithVatDocumentMutation,
} = businessCasePaymentsApi;
